import { Injectable } from '@angular/core';
import { MediaFile, MediaType, ObservableValue, ResourceState, GroupState, ResourceStatus, GroupStateMap, CacheProgress, GroupStatus } from '../../models';
import { ProgressManager } from '../../models/progress-manager';
import { UploadService, UploadStatus, UploadState } from '../../providers';
import { map, debounceTime } from 'rxjs/operators';
import { Observable } from 'rxjs';

interface UploadItem {
    file: MediaFile,
    observer: ObservableValue<ResourceState<MediaFile>>,
}
@Injectable({ providedIn: 'root' })
export class UploadManagerService {

    public allowedTypes: MediaType[] = [MediaType.IMAGE, MediaType.VIDEO, MediaType.SOUND, MediaType.AMBISONIC];
    public uploadMap: { [key: string]: UploadItem; } = {};
    public totalProgress: number = 0;

    private uploadId = 0;
    private progressManager: ProgressManager<MediaFile> = new ProgressManager<MediaFile>();

    private readonly customMetadata = {
        contentType: "image/jpeg",
        contentLanguage: "en",
        customMetadata: {
            app: "motes-pro",
            createdBy: 'not-set'
        },
        cacheControl: "public, max-age=31536000, s-maxage=31536000, immutable"
    };

    constructor(private uploadService: UploadService) {
        this.progressManager.init();
    }

    public startUpload(file: MediaFile, groupId?: string): GroupState {
        if(!file || !file.blob) {
            console.error("Cannot start upload of empty file or file without blob: ", file ? file.blob : 'file is null');
            return null;
        }

        // Dynamically create group unique group id if non exist
        if(!groupId) {
            this.uploadId++;
            groupId = "dyn_upload_" + this.uploadId
        }
        let group = this.progressManager.getResourceGroup(groupId);

        // Create a new upload group
        if(!group) group = this.progressManager.initResourceGroup(groupId);
        let observer = this.createUploadObserver(group, file);
        group.resourceList.push(observer);

        this.progressManager.addResource(observer);
        this.uploadMap[groupId + "_" + file.type] = {
            file,
            observer,
        };

        // Return progress of first (and only) resource in group
        // return this.progressManager.watchGroupProgress(group).pipe(map(resourceList => resourceList[0]));
        return group;
    }

    public startMultiUpload(files: MediaFile[], groupId?: string) {
        for(let file of files) {
            this.startUpload(file, groupId);
        }
    }

    public watchUploadGroupProgress(): Observable<GroupStateMap> {
        return this.progressManager.watchResourceMap();
    }

    public watchUploadGroupResult(groupId: string): Observable<ResourceState<MediaFile>[]> {
        let group = this.progressManager.getResourceGroup(groupId);
        if(!group) group = this.progressManager.initResourceGroup(groupId);
        // Pipe result through debounce to let other call backs to their work (ie. update the media object) before notifying listeners
        return this.progressManager.watchGroupResult(group).pipe(debounceTime(100));
    }

    public watchUploadGroupResults(): Observable<ResourceState<MediaFile>[]> {
        // Pipe result through debounce to let other call backs to their work (ie. update the media object) before notifying listeners
        return this.progressManager.watchGroupResults().pipe(debounceTime(100));
    }

    public watchUploadErrors(): Observable<ResourceState<MediaFile>> {
        return this.progressManager.watchResourceErrors();
    }

    public watchUploadProgress(): Observable<CacheProgress> {
        return this.progressManager.watchCachingProgress();
    }

    private createUploadObserver(group: GroupState, file: MediaFile): ObservableValue<ResourceState<MediaFile>> {
        if(!file || !file.blob) {
            console.warn("Coupd not create upload observer (no file to upload)");
            return null;
        }

        let resource: ResourceState<MediaFile> = this.progressManager.createResource(group, file.url, "Media Upload " + this.uploadId);

        let obs = new ObservableValue<ResourceState>(observer => {
            // Start request
            resource.percentage = 0;
            observer.next(resource);

            let metaData = this.customMetadata;
            metaData.contentType = file.blob.type;

            if(!file.storage) {
                observer.error(`Cannot upload file without storage path (file.storage of ${file.type} is empty)`);
                return;
            }

            this.uploadService.uploadFile(file.blob, file.storage, metaData).pipe(map((uploadState: UploadState) => {
                resource.bytesTotal = uploadState.bytesTotal;
                resource.bytesTransfered = uploadState.bytesTransfered;
                resource.percentage = uploadState.percentage;
                resource.error = uploadState.error;
                resource.status = this.uploadToProgressStatus(uploadState.status);
                resource.context = file;
                observer.next(resource);

                if(uploadState.status == UploadStatus.DONE) {
                    // Save download url in file
                    file.url = uploadState.downloadUrl;
                    file.storage = uploadState.storageRef;
                    observer.complete();
                }

            })).subscribe();
        });
        // Init resource state
        obs.lastVal = resource;
        return obs;
    }

    private uploadToProgressStatus(uploadStatus: UploadStatus): ResourceStatus {
        switch(uploadStatus) {
            case UploadStatus.IDLE: return 'Idle';
            case UploadStatus.RUNNING: return 'Downloading';
            case UploadStatus.ERROR: return 'Error';
            case UploadStatus.DONE: return 'Cached';
            default: return 'Error';
        }
    }

    public clearUploadList() {
        this.uploadMap = {};
    }

    public cancelUpload() {
        this.uploadMap = {};
    }
}
